import {AuthenticationResult} from '@azure/msal-browser';
import type {User, Role, UserPermissions, EkcoUserWithRoles} from '@/api/apolloPlatform/user';

// Keep in sync with apollo_platform/apps/users/models.py
const TEST_EKCO_USER_EMAILS = new Set([
  'sphere.admin.1@ekcoiredev1.onmicrosoft.com',
  'sphere.admin.2@ekcoiredev1.onmicrosoft.com',
  'sphere.superuser@ekcoiredev1.onmicrosoft.com',
]);

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null);
  const ekcoUsers = ref<EkcoUserWithRoles[] | null>(null);
  const ekcoUsersWithRoles = ref<EkcoUserWithRoles[] | null>(null);
  const ekcoEmployeeRoles = ref<Role[] | null>(null);
  const msalToken = ref<AuthenticationResult | null>(null);
  const roles = ref<Role[] | null>(null);
  const customerRoles = ref<Role[] | null>(null);

  const userPermissions = ref<UserPermissions | null>(null);
  let permissionsLoading: Promise<UserPermissions> | null = null;
  const permissionsLoaded = computed(() => userPermissions.value !== null);

  const getEkcoUsers = computed(() => ekcoUsers.value ?? []);
  const getEkcoUsersWithRoles = computed(() => ekcoUsersWithRoles.value ?? []);
  const getEkcoEmployeeRoles = computed(() => ekcoEmployeeRoles.value ?? []);
  const isEkcoUser = computed(() => {
    const email = user.value?.email;
    if (!email) return false;
    return email.endsWith('@ek.co') || TEST_EKCO_USER_EMAILS.has(email);
  });
  const isSuperUser = computed(() => user.value?.is_superuser ?? false);
  const isStaffUser = computed(() => user.value?.is_staff ?? false);

  /**
   * Return true if the user has a permission or false if they don't.
   * Return undefined if the user's permissions are unknown.
   */

  const hasPermission = (permission: string) => {
    if (userPermissions.value === null) return undefined;
    return userPermissions.value?.user_permissions.includes(permission);
  };

  const hasAnyPermission = (permissions: string[]) => {
    if (userPermissions.value === null) return undefined;
    return permissions.some((perm) => userPermissions.value?.user_permissions.includes(perm));
  };

  const hasAnyGroup = (groupNames: string | string[]) => {
    if (!user.value?.groups?.length) return false;
    const names = Array.isArray(groupNames) ? groupNames : [groupNames];
    const userGroupNames = new Set(user.value?.groups.map((g) => g.name));
    return names.some((name) => userGroupNames.has(name));
  };

  const isUserManager = computed(
    () =>
      hasPermission('edit:user_management') ||
      hasAnyGroup(['invitation_managers', 'role_managers', 'staff_group_managers']) ||
      false,
  );

  const isContactManager = computed(() => isEkcoUser.value && isUserManager.value);

  const isEkcoUserManager = computed(
    () =>
      isEkcoUser &&
      (hasPermission('edit:ekco_user_management') ||
        hasAnyGroup(['role_managers', 'staff_group_managers']) ||
        false),
  );

  const isCustomerProductManager = computed(
    () => isEkcoUser.value && hasPermission('edit:customer_products'),
  );

  const isCustomerMappingManager = computed(
    () =>
      (isEkcoUser.value &&
        (hasAnyGroup('customer_mapping_managers') || hasPermission('edit:customer_mapping'))) ||
      false,
  );

  const canExportReports = computed(
    () => (isEkcoUser.value && hasPermission('export:reports')) || false,
  );

  const isCmsEditor = computed(
    () => isSuperUser.value || (isEkcoUser.value && hasAnyGroup(['CMS Editors', 'CMS Moderators'])),
  );

  const readUser = async () => {
    const [userResponse, settingsResponse] = await Promise.all([
      apiGetUser(),
      apiGetUserSettings(),
    ]);
    user.value = userResponse.data;
    useUserSettingsStore().replace(settingsResponse.data);
    return userResponse.data as User; // TODO: Update API module return types.
  };

  const readEkcoUsers = async () => {
    const {data} = await apiGetEkcoUsers();
    ekcoUsers.value = data;
  };

  const readEkcoUsersWithRoles = async () => {
    try {
      const {data} = await apiGetEkcoUsersWithRoles();
      ekcoUsersWithRoles.value = data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const readEkcoEmployeeRoles = async () => {
    try {
      const {data} = await apiGetEkcoEmployeeRoles();
      ekcoEmployeeRoles.value = data;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const updateDirectRoles = async (userId: number, changes: {add: number[]; remove: number[]}) => {
    try {
      let updatedUser: EkcoUserWithRoles | undefined;

      // Perform removals first
      if (changes.remove.length > 0) {
        const {data} = await apiRemoveDirectRoleAssignments(userId, changes.remove);
        updatedUser = data;
      }

      // Then perform additions (this will be the final state)
      if (changes.add.length > 0) {
        const {data} = await apiAddDirectRoleAssignments(userId, changes.add);
        updatedUser = data;
      }

      // Update the user in the list if we have the updated data
      if (updatedUser && ekcoUsersWithRoles.value) {
        const index = ekcoUsersWithRoles.value.findIndex((u) => u.id === userId);
        if (index !== -1) {
          ekcoUsersWithRoles.value[index] = updatedUser;
        }
      }

      return updatedUser;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const setRolesAndPermissions = (value: Role[]) => {
    roles.value = value;
    const allPermissions = value.flatMap((role) =>
      role.permissions.map((permission) => permission.key),
    );
    const uniquePermissions = [...new Set(allPermissions)];
    userPermissions.value = {user_permissions: uniquePermissions};
  };

  const readUserPermissions = () => {
    if (!permissionsLoading) {
      permissionsLoading = (async () => {
        try {
          const {data} = await apiGetUserRoles();
          setRolesAndPermissions(data.roles);
          return userPermissions.value!;
        } catch (error) {
          throw apiErrorMessage(error);
        } finally {
          permissionsLoading = null;
        }
      })();
    }
    return permissionsLoading;
  };

  const readCustomerRoles = async () => {
    try {
      const {data} = await apiGetCustomerRoles();
      customerRoles.value = data.roles;
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const createUserRoleAssignments = async (id: number, roleIds: number[]) => {
    try {
      await apiPostUserRoleAssignments(id, roleIds);
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const deleteUserRoleAssignment = async (id: number, roleId: number) => {
    try {
      await apiDeleteUserRoleAssignment(id, roleId);
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const deleteUserCustomerRelation = async (id: number, customerId: number) => {
    try {
      await apiDeleteUserCustomerRelation(id, customerId);
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  const deactivateEkcoUser = async (userId: number) => {
    try {
      await apiDeactivateEkcoUser(userId);
      if (ekcoUsersWithRoles.value) {
        ekcoUsersWithRoles.value = ekcoUsersWithRoles.value.filter((u) => u.id !== userId);
      }
    } catch (error) {
      throw apiErrorMessage(error);
    }
  };

  // N.B. Used in App.vue
  const setMsalToken = (value: AuthenticationResult | undefined) => {
    msalToken.value = value ?? null;
  };

  return {
    user,
    ekcoUsers,
    ekcoUsersWithRoles,
    ekcoEmployeeRoles,
    msalToken,
    roles,
    customerRoles,
    userPermissions,
    permissionsLoading,
    permissionsLoaded,
    getEkcoUsers,
    getEkcoUsersWithRoles,
    getEkcoEmployeeRoles,
    isEkcoUser,
    isSuperUser,
    isStaffUser,
    hasPermission,
    hasAnyPermission,
    hasAnyGroup,
    isUserManager,
    isContactManager,
    isEkcoUserManager,
    canExportReports,
    isCustomerMappingManager,
    isCmsEditor,
    isCustomerProductManager,
    readUser,
    readEkcoUsers,
    readEkcoUsersWithRoles,
    readEkcoEmployeeRoles,
    updateDirectRoles,
    setRolesAndPermissions,
    readUserPermissions,
    readCustomerRoles,
    createUserRoleAssignments,
    deleteUserRoleAssignment,
    deleteUserCustomerRelation,
    deactivateEkcoUser,
    setMsalToken,
  };
});
